import React, { useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Loader2 } from "lucide-react";
import { notificationsApi } from "../../../lib/api";
import { TID } from "../../../constants/testIds";
import { getApiError } from "../../../lib/utils";

export const ReportIssueModal = ({ facility, onClose, onSubmitted }) => {
  const [msg, setMsg] = useState("");
  const [severity, setSeverity] = useState("MEDIUM");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (msg.trim().length < 5) {
      toast.error("Message must be at least 5 characters");
      return;
    }
    setBusy(true);
    try {
      await notificationsApi.create({ facility_id: facility.facility_id, message: msg.trim(), severity });
      toast.success("Report filed. The HSP team has been alerted.");
      onSubmitted();
    } catch (e) {
      toast.error(getApiError(e, "Failed to file report"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-md border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <AlertCircle size={18} className="text-amber-600" strokeWidth={2.5} />
            <h3 className="font-heading text-base font-bold text-slate-900">Report an issue</h3>
          </div>
          <p className="mt-1 text-xs text-slate-600">
            {facility.name} · <span className="font-mono">{facility.facility_id}</span>
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Severity</label>
            <select
              data-testid={TID.reportIssueSeverity}
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
            >
              <option value="LOW">Low — for info</option>
              <option value="MEDIUM">Medium — needs review</option>
              <option value="HIGH">High — urgent review</option>
              <option value="URGENT">Urgent — likely suspension needed</option>
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">What happened?</label>
            <textarea
              data-testid={TID.reportIssueMessage}
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              rows={5}
              className="w-full resize-none rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
              placeholder="Describe what you observed. Include date/time and any member IDs if applicable."
            />
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-900">
            The HSP admin team will receive this immediately via Telegram and see it in the Notifications inbox.
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button onClick={onClose} disabled={busy} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button
            data-testid={TID.reportIssueSubmit}
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <AlertCircle size={12} strokeWidth={2.5} />}
            File report
          </button>
        </div>
      </div>
    </div>
  );
};
